import {AdminLayout} from "../../layouts/admin-layout";
import {useEffect, useMemo, useRef, useState} from "react";
import {BuildWarning, BuildWarningRisk, getBuild, getBuildWarnings} from "../../services/buildsService";
import {isAxiosError} from "axios";

export function BuildsPage() {
    const [warnings, setWarnings] = useState<BuildWarning[]>([]);
    const [warningsLoading, setWarningsLoading] = useState<boolean>(true);

    const [zigBuilding, setZigBuilding] = useState<boolean>(false);
    const [zigError, setZigError] = useState<string | null>(null);
    const [zigZipPassword, setZigZipPassword] = useState<string | null>(null);
    const [zigPasswordCopied, setZigPasswordCopied] = useState<boolean>(false);
    const zigPasswordCopyTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setWarningsLoading(true);
            try {
                const res = await getBuildWarnings();
                if (!mounted) return;
                setWarnings(Array.isArray(res.warnings) ? res.warnings : []);
            } catch {
                if (!mounted) return;
                setWarnings([]);
            } finally {
                if (mounted) {
                    setWarningsLoading(false);
                }
            }
        })();

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        return () => {
            if (zigPasswordCopyTimeoutRef.current) {
                window.clearTimeout(zigPasswordCopyTimeoutRef.current);
            }
        };
    }, []);

    const hasRedWarning = useMemo(() => warnings.some((w) => w.risk === "red"), [warnings]);

    const worstWarningRisk = useMemo<BuildWarningRisk>(() => {
        if (warnings.some((w) => w.risk === "red")) return "red";
        if (warnings.some((w) => w.risk === "yellow")) return "yellow";
        return "blue";
    }, [warnings]);

    const warningsCountLabel = useMemo(() => {
        const count = warnings.length;
        return `${count} Active`;
    }, [warnings.length]);

    const getWarningsPanelTheme = (risk: BuildWarningRisk) => {
        if (risk === "red") {
            return {
                bg: "bg-[rgba(30,15,15,0.4)]",
                border: "border-red-500/20",
                hoverBorder: "hover:border-red-500/30",
                orb: "bg-red-500/10",
                topLineVia: "via-red-500/20",
                iconBox: "bg-red-500/10 text-red-400 ring-1 ring-red-500/20 shadow-[0_0_15px_rgba(239,68,68,0.15)]",
                iconGlow: "bg-red-500/20",
                title: "text-red-100",
                badge: "bg-red-500/10 text-red-400 border border-red-500/10 shadow-[0_0_10px_rgba(239,68,68,0.1)]",
            };
        }

        if (risk === "blue") {
            return {
                bg: "bg-[rgba(15,20,30,0.4)]",
                border: "border-blue-500/20",
                hoverBorder: "hover:border-blue-500/30",
                orb: "bg-blue-500/10",
                topLineVia: "via-blue-500/20",
                iconBox: "bg-blue-500/10 text-blue-400 ring-1 ring-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.15)]",
                iconGlow: "bg-blue-500/20",
                title: "text-blue-100",
                badge: "bg-blue-500/10 text-blue-400 border border-blue-500/10 shadow-[0_0_10px_rgba(59,130,246,0.1)]",
            };
        }

        return {
            bg: "bg-[rgba(30,25,15,0.4)]",
            border: "border-amber-500/20",
            hoverBorder: "hover:border-amber-500/30",
            orb: "bg-amber-500/10",
            topLineVia: "via-amber-500/20",
            iconBox: "bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/20 shadow-[0_0_15px_rgba(245,158,11,0.15)]",
            iconGlow: "bg-amber-500/20",
            title: "text-amber-100",
            badge: "bg-amber-500/10 text-amber-500 border border-amber-500/10 shadow-[0_0_10px_rgba(245,158,11,0.1)]",
        };
    };

    const getWarningClasses = (risk: BuildWarningRisk) => {
        if (risk === "red") {
            return {
                dot: "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]",
                item: "hover:bg-red-500/5 hover:border-red-500/20",
            };
        }
        if (risk === "blue") {
            return {
                dot: "bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.8)]",
                item: "hover:bg-blue-500/5 hover:border-blue-500/20",
            };
        }
        return {
            dot: "bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.8)]",
            item: "hover:bg-amber-500/5 hover:border-amber-500/20",
        };
    };

    const downloadBlob = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => {
            window.URL.revokeObjectURL(url);
        }, 0);
    };

    const handleCopyZigPassword = async () => {
        if (!zigZipPassword) {
            return;
        }
        try {
            await navigator.clipboard.writeText(zigZipPassword);
            setZigPasswordCopied(true);
            if (zigPasswordCopyTimeoutRef.current) {
                window.clearTimeout(zigPasswordCopyTimeoutRef.current);
            }
            zigPasswordCopyTimeoutRef.current = window.setTimeout(() => {
                setZigPasswordCopied(false);
            }, 1500);
        } catch {
            // no-op
        }
    };

    const handleBuildZig = async () => {
        if (zigBuilding || hasRedWarning) {
            return;
        }

        setZigBuilding(true);
        setZigError(null);
        setZigZipPassword(null);
        setZigPasswordCopied(false);

        try {
            const res = await getBuild({type: "zig"});
            if (!res.success) {
                setZigError(res.error || "Failed to build program");
                return;
            }

            downloadBlob(res.blob, "zig-build.zip");
            setZigZipPassword(res.zipPassword);
        } catch (err) {
            if (isAxiosError(err)) {
                setZigError(err.response?.status ? `Build failed (${err.response.status})` : err.message);
                return;
            }
            setZigError("Failed to build program");
        } finally {
            setZigBuilding(false);
        }
    };

    const disableBuildButtons = hasRedWarning || zigBuilding;

    return (
        <AdminLayout>
            <div className="w-full max-w-[800px] flex flex-col gap-8 animate-[fadeIn_0.5s_ease-out]">
                <div className="flex flex-col gap-2 text-center sm:text-left">
                    <h2 className="text-4xl font-bold tracking-tight text-white drop-shadow-sm">Builds</h2>
                    <p className="text-lg text-slate-400">Manage your program compilations.</p>
                </div>

                {warningsLoading ? (
                    <div className="flex items-center justify-center py-16">
                        <div className="flex items-center gap-3 text-slate-400">
                            <span className="w-6 h-6 border-2 border-white/15 border-t-white/70 rounded-full animate-spin"></span>
                            <span className="text-sm font-medium">Loading…</span>
                        </div>
                    </div>
                ) : (
                    <>
                        {warnings.length > 0 && (
                            (() => {
                                const theme = getWarningsPanelTheme(worstWarningRisk);
                                return (
                            <div
                                className={`relative overflow-hidden rounded-2xl ${theme.bg} backdrop-blur-xl border ${theme.border} shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] transition-all ${theme.hoverBorder} group/panel`}>
                                <div
                                    className={`absolute -top-10 -right-10 w-32 h-32 ${theme.orb} rounded-full blur-3xl pointer-events-none opacity-50 group-hover/panel:opacity-80 transition-opacity`}></div>
                                <div
                                    className={`absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent ${theme.topLineVia} to-transparent`}></div>
                                <div className="p-5 flex flex-col gap-4 relative z-10">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div
                                                className={`relative flex items-center justify-center w-8 h-8 rounded-lg ${theme.iconBox}`}>
                                                <span className="material-symbols-outlined text-lg">warning</span>
                                                <div
                                                    className={`absolute inset-0 ${theme.iconGlow} blur-md rounded-lg -z-10 animate-pulse`}></div>
                                            </div>
                                            <h3 className={`text-sm font-semibold ${theme.title} tracking-wide uppercase`}>System
                                                Warnings</h3>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${theme.badge}`}>{warningsCountLabel}</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        {warnings.map((warning, idx) => {
                                            const cfg = getWarningClasses(warning.risk);
                                            return (
                                                <div
                                                    key={`${warning.risk}-${warning.title}-${idx}`}
                                                    className={`group flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl bg-black/20 border border-white/5 transition-all duration-300 relative overflow-hidden ${cfg.item}`}
                                                >
                                                    <div className="flex items-center gap-3 z-10">
                                                        <div className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></div>
                                                        <span className="text-xs font-medium text-white/90 whitespace-nowrap">{warning.title}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between gap-4 flex-grow z-10">
                                                        <span
                                                            className="text-[11px] text-white/50 sm:border-l sm:border-white/10 sm:pl-3 line-clamp-1">{warning.message}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                                );
                            })()
                        )}
                        <div className="glass-panel rounded-3xl p-1 overflow-hidden">
                            <div className="bg-black/20 rounded-[20px] p-6 sm:p-8 flex flex-col gap-8">
                                <div
                                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-white/5">
                                    <div className="flex flex-col gap-1">
                                        <h3 className="text-xl font-semibold text-white">Active Configurations</h3>
                                        <p className="text-sm text-slate-400">Source code ready for compilation</p>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-4">
                                    <div
                                        className="flex flex-col md:flex-row items-start md:items-center justify-between p-5 rounded-2xl bg-white/5 hover:bg-white/[0.07] transition-all border border-white/5 group relative overflow-hidden gap-6">
                                        <div
                                            className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                        <div className="relative flex items-center gap-5 z-10">
                                            <div
                                                className="flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-2xl bg-[#f7a41d]/10 border border-[#f7a41d]/20 shadow-[0_0_15px_rgba(247,164,29,0.15)] text-[#f7a41d]">
                                                <span className="material-symbols-outlined text-3xl">electric_bolt</span>
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-3">
                                                    <h4 className="text-xl font-bold text-white tracking-tight">Zig</h4>
                                                    <span
                                                        className="px-2 py-0.5 rounded text-[10px] font-bold bg-white/10 text-white/60 border border-white/5">v1.1.0</span>
                                                </div>
                                                <p className="text-sm text-slate-400">Stable build. No autorun.</p>
                                            </div>
                                        </div>
                                        <div className="relative z-10 w-full md:w-auto flex justify-end">
                                            <div className="flex flex-col items-end gap-2 w-full md:w-auto">
                                                <button
                                                    onClick={handleBuildZig}
                                                    disabled={disableBuildButtons}
                                                    className="w-full md:w-auto relative overflow-hidden group rounded-xl bg-primary px-6 py-3 font-semibold text-white shadow-[0_0_20px_rgba(19,91,236,0.3)] transition-all hover:shadow-[0_0_30px_rgba(19,91,236,0.5)] hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed">
                                                    <div
                                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:animate-[shimmer_1.5s_infinite]"></div>
                                                    <span className="relative flex items-center justify-center gap-2">
                                                        {zigBuilding ? (
                                                            <span className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></span>
                                                        ) : (
                                                            <span className="material-symbols-outlined">construction</span>
                                                        )}
                                                        {zigBuilding ? "Building..." : "Build Program"}
                                                    </span>
                                                </button>

                                                {zigError && (
                                                    <p className="text-xs text-rose-400 text-right">{zigError}</p>
                                                )}

                                                {!zigError && zigZipPassword && (
                                                    <div className="flex items-center gap-2 text-xs text-emerald-400">
                                                        <span className="material-symbols-outlined text-sm">lock</span>
                                                        <span>ZIP password:</span>
                                                        <span className="font-mono text-emerald-200">{zigZipPassword}</span>
                                                        <button
                                                            onClick={handleCopyZigPassword}
                                                            title={zigPasswordCopied ? "Copied!" : "Copy password"}
                                                            className={`transition-colors ${
                                                                zigPasswordCopied ? "text-emerald-300" : "text-emerald-400/70 hover:text-emerald-300"
                                                            }`}
                                                        >
                                                            <span className="material-symbols-outlined text-sm">
                                                                {zigPasswordCopied ? "done" : "content_copy"}
                                                            </span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </AdminLayout>
    )
}
